/**
 * PhotoSelector: Display user's photo collection for markup test page
 * Feature: 049-critique-photo-markup
 */

import { usePhotosGraphQL } from '@/graphql/hooks/usePhotosGraphQL';
import { Heading } from '@/components/atoms/Heading';
import { Text } from '@/components/atoms/Text';
import logger from '@/utils/logger';

interface PhotoSelectorProps {
  selectedPhotoId: string | null;
  onPhotoSelect: (photoId: string) => void;
}

export function PhotoSelector({ selectedPhotoId, onPhotoSelect }: PhotoSelectorProps) {
  const { photos, loading, error } = usePhotosGraphQL();

  if (loading && photos.length === 0) {
    return (
      <div className="p-4">
        <Text>Loading photos...</Text>
      </div>
    );
  }

  if (error) {
    logger.error('PhotoSelector', 'Failed to load photos:', error);
    return (
      <div className="p-4">
        <Text className="text-danger">Failed to load photos</Text>
      </div>
    );
  }

  if (photos.length === 0) {
    return (
      <div className="p-4">
        <Text>No photos found. Upload some photos first.</Text>
      </div>
    );
  }

  return (
    <div className="p-4">
      <Heading level="h3" className="mb-4">
        Select a Photo
      </Heading>
      <div className="grid grid-cols-2 gap-4 max-h-[600px] overflow-y-auto">
        {photos.map((photo) => (
          <button
            key={photo.id}
            onClick={() => {
              logger.info('PhotoSelector', 'Selected photo:', photo.id);
              onPhotoSelect(photo.id);
            }}
            className={`relative aspect-video overflow-hidden rounded border-2 transition-all hover:border-primary ${
              selectedPhotoId && selectedPhotoId === photo.id ? 'border-primary ring-2 ring-primary' : 'border-border'
            }`}
          >
            <img
              src={photo.variants?.find(v => v.variantType === 'thumbnail')?.storageUrl
                || photo.variants?.[0]?.storageUrl
                || ''}
              alt={photo.filename}
              className="w-full h-full object-cover"
            />
            <div className="absolute bottom-0 left-0 right-0 bg-black/60 p-2">
              <Text className="text-white text-xs truncate">{photo.filename}</Text>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
